import { MAGELUP_PDF_OWNER_PASSWORD } from 'constants/pdfPasswordProtection';

interface IPdfPageViewport {
  width: number;
  height: number;
}

interface IPdfPage {
  getViewport: (params: { scale: number }) => IPdfPageViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    viewport: IPdfPageViewport;
  }) => { promise: Promise<void> };
}

interface IPdfDocument {
  numPages: number;
  getPage: (pageNumber: number) => Promise<IPdfPage>;
  destroy: () => void;
}

interface IPdfjsModule {
  version: string;
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (params: {
    data: Uint8Array;
    password?: string;
  }) => { promise: Promise<IPdfDocument> };
}

export const getPdfPasswordCandidates = (
  passwords: Array<string | null | undefined> = [],
): string[] =>
  [...passwords, MAGELUP_PDF_OWNER_PASSWORD].filter(
    (password): password is string => !!password?.trim(),
  );

/**
 * Tries to open a PDF blob, optionally with password candidates
 * (label PESEL/NIP + MagelUp owner password).
 */
export const openPdfDocument = async (
  blob: Blob,
  passwords: Array<string | null | undefined> = [],
): Promise<IPdfDocument | null> => {
  try {
    const pdfjs = (await import('pdfjs-dist/build/pdf')) as IPdfjsModule;
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

    const data = new Uint8Array(await blob.arrayBuffer());
    const candidates = ['', ...getPdfPasswordCandidates(passwords)];

    for (const password of candidates) {
      try {
        const loadingTask = pdfjs.getDocument({
          data: data.slice(0),
          ...(password ? { password } : {}),
        });
        return await loadingTask.promise;
      } catch (error: unknown) {
        const name =
          error && typeof error === 'object' && 'name' in error
            ? String((error as { name?: string }).name)
            : '';
        const message =
          error instanceof Error ? error.message : String(error ?? '');

        const isPasswordError =
          name === 'PasswordException' ||
          message.includes('PasswordException') ||
          message.includes('NeedPassword') ||
          message.includes('Incorrect Password');

        if (isPasswordError) {
          continue;
        }

        return null;
      }
    }
  } catch {
    return null;
  }

  return null;
};

export const renderPdfPagesToContainer = async (
  pdf: IPdfDocument,
  container: HTMLElement,
): Promise<void> => {
  container.replaceChildren();

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1.25 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      continue;
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = '100%';
    canvas.style.height = 'auto';
    canvas.style.marginBottom = '12px';
    container.appendChild(canvas);

    await page.render({ canvasContext: context, viewport }).promise;
  }
};
